import { useState, useEffect, useCallback } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import {
  Page,
  Layout,
  Card,
  Text,
  BlockStack,
  InlineStack,
  Badge,
  Button,
  Select,
  TextField,
  Thumbnail,
  Divider,
  Spinner,
  Banner,
  Box,
  Modal,
} from '@shopify/polaris';
import {
  inquiryService,
  type InquiryDetails,
  type InquiryStatus,
  type Message,
} from '../../services/inquiry.service';
import { QuoteBuilder } from '../../components/admin/QuoteBuilder';

const STATUS_OPTIONS: { label: string; value: InquiryStatus }[] = [
  { label: 'Pending', value: 'PENDING' },
  { label: 'Under Review', value: 'UNDER_REVIEW' },
  { label: 'Quote Sent', value: 'QUOTE_SENT' },
  { label: 'Quote Accepted', value: 'QUOTE_ACCEPTED' },
  { label: 'Quote Declined', value: 'QUOTE_DECLINED' },
  { label: 'Revision Requested', value: 'QUOTE_REVISION_REQUESTED' },
  { label: 'In Progress', value: 'IN_PROGRESS' },
  { label: 'Completed', value: 'COMPLETED' },
  { label: 'Cancelled', value: 'CANCELLED' },
];

function getStatusBadge(status: InquiryStatus) {
  const toneMap: Record<InquiryStatus, 'warning' | 'info' | 'success' | 'critical' | 'attention' | undefined> = {
    PENDING: 'warning',
    UNDER_REVIEW: 'info',
    QUOTE_SENT: 'info',
    QUOTE_ACCEPTED: 'success',
    QUOTE_DECLINED: 'critical',
    QUOTE_REVISION_REQUESTED: 'attention',
    IN_PROGRESS: 'info',
    COMPLETED: 'success',
    CANCELLED: 'critical',
  };

  return <Badge tone={toneMap[status]}>{status.replace(/_/g, ' ')}</Badge>;
}

function formatDate(dateStr: string): string {
  return new Date(dateStr).toLocaleDateString('en-IN', {
    year: 'numeric',
    month: 'short',
    day: 'numeric',
    hour: '2-digit',
    minute: '2-digit',
  });
}

function formatCurrency(amount: number): string {
  return new Intl.NumberFormat('en-IN', {
    style: 'currency',
    currency: 'INR',
    maximumFractionDigits: 0,
  }).format(amount);
}

export function InquiryDetailPage() {
  const { id } = useParams<{ id: string }>();
  const navigate = useNavigate();
  const [inquiry, setInquiry] = useState<InquiryDetails | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [saving, setSaving] = useState(false);

  // Status update
  const [selectedStatus, setSelectedStatus] = useState<InquiryStatus | ''>('');

  // Message
  const [newMessage, setNewMessage] = useState('');
  const [sendingMessage, setSendingMessage] = useState(false);

  // Quote builder modal
  const [showQuoteBuilder, setShowQuoteBuilder] = useState(false);
  const [sendingQuote, setSendingQuote] = useState(false);

  const loadInquiry = useCallback(async () => {
    if (!id) return;

    try {
      setLoading(true);
      setError('');
      const data = await inquiryService.getById(id);
      setInquiry(data);
      setSelectedStatus(data.status);
    } catch (err) {
      setError('Failed to load inquiry');
      console.error(err);
    } finally {
      setLoading(false);
    }
  }, [id]);

  useEffect(() => {
    loadInquiry();
  }, [loadInquiry]);

  const handleStatusUpdate = async () => {
    if (!inquiry || !selectedStatus || selectedStatus === inquiry.status) return;

    try {
      setSaving(true);
      const updated = await inquiryService.updateStatus(inquiry.id, selectedStatus);
      setInquiry(updated);
    } catch (err) {
      setError('Failed to update status');
      console.error(err);
    } finally {
      setSaving(false);
    }
  };

  const handleSendMessage = async () => {
    if (!inquiry || !newMessage.trim()) return;

    try {
      setSendingMessage(true);
      await inquiryService.addMessage(inquiry.id, newMessage.trim());
      setNewMessage('');
      await loadInquiry();
    } catch (err) {
      setError('Failed to send message');
      console.error(err);
    } finally {
      setSendingMessage(false);
    }
  };

  const handleQuoteCreated = async () => {
    setShowQuoteBuilder(false);
    await loadInquiry();
  };

  const handleSendQuote = async () => {
    if (!inquiry) return;

    try {
      setSendingQuote(true);
      const updated = await inquiryService.sendQuote(inquiry.id);
      setInquiry(updated);
    } catch (err) {
      setError('Failed to send quote');
      console.error(err);
    } finally {
      setSendingQuote(false);
    }
  };

  if (loading) {
    return (
      <Page title="Loading...">
        <Card>
          <Box padding="400">
            <InlineStack align="center">
              <Spinner />
            </InlineStack>
          </Box>
        </Card>
      </Page>
    );
  }

  if (!inquiry) {
    return (
      <Page title="Inquiry Not Found">
        <Card>
          <Text as="p">The requested inquiry could not be found.</Text>
          <Button onClick={() => navigate('/inquiries')}>Back to Inquiries</Button>
        </Card>
      </Page>
    );
  }

  return (
    <Page
      backAction={{ content: 'Inquiries', onAction: () => navigate('/inquiries') }}
      title={`Inquiry ${inquiry.referenceNo}`}
      titleMetadata={getStatusBadge(inquiry.status)}
      secondaryActions={[
        {
          content: inquiry.quote ? 'Edit Quote' : 'Create Quote',
          onAction: () => setShowQuoteBuilder(true),
        },
      ]}
    >
      {error && (
        <Layout.Section>
          <Banner tone="critical" onDismiss={() => setError('')}>
            {error}
          </Banner>
        </Layout.Section>
      )}

      <Layout>
        {/* Main Content */}
        <Layout.Section>
          {/* Customer & Inquiry Info */}
          <Card>
            <BlockStack gap="400">
              <Text as="h2" variant="headingMd">Inquiry Details</Text>

              <InlineStack gap="800">
                <BlockStack gap="100">
                  <Text as="p" tone="subdued" variant="bodySm">Customer</Text>
                  <Text as="p" variant="bodyMd" fontWeight="semibold">
                    {inquiry.user.name || inquiry.user.email}
                  </Text>
                  <Text as="p" variant="bodySm">{inquiry.user.email}</Text>
                  {inquiry.user.companyName && (
                    <Text as="p" variant="bodySm" tone="subdued">
                      {inquiry.user.companyName}
                    </Text>
                  )}
                </BlockStack>

                <BlockStack gap="100">
                  <Text as="p" tone="subdued" variant="bodySm">Type</Text>
                  <Badge tone={inquiry.type === 'BULK_ORDER' ? 'info' : undefined}>
                    {inquiry.type === 'BULK_ORDER' ? 'Bulk Order' : 'Sample Request'}
                  </Badge>
                </BlockStack>

                <BlockStack gap="100">
                  <Text as="p" tone="subdued" variant="bodySm">Created</Text>
                  <Text as="p" variant="bodyMd">{formatDate(inquiry.createdAt)}</Text>
                </BlockStack>
              </InlineStack>

              {(inquiry.deliveryAddress || inquiry.logoDescription || inquiry.timeline || inquiry.notes) && (
                <>
                  <Divider />
                  <BlockStack gap="200">
                    {inquiry.deliveryAddress && (
                      <BlockStack gap="100">
                        <Text as="p" tone="subdued" variant="bodySm">Delivery Address</Text>
                        <Text as="p" variant="bodyMd">{inquiry.deliveryAddress}</Text>
                      </BlockStack>
                    )}
                    {inquiry.logoDescription && (
                      <BlockStack gap="100">
                        <Text as="p" tone="subdued" variant="bodySm">Logo/Branding</Text>
                        <Text as="p" variant="bodyMd">{inquiry.logoDescription}</Text>
                      </BlockStack>
                    )}
                    {inquiry.timeline && (
                      <BlockStack gap="100">
                        <Text as="p" tone="subdued" variant="bodySm">Timeline</Text>
                        <Text as="p" variant="bodyMd">{inquiry.timeline}</Text>
                      </BlockStack>
                    )}
                    {inquiry.notes && (
                      <BlockStack gap="100">
                        <Text as="p" tone="subdued" variant="bodySm">Additional Notes</Text>
                        <Text as="p" variant="bodyMd">{inquiry.notes}</Text>
                      </BlockStack>
                    )}
                  </BlockStack>
                </>
              )}
            </BlockStack>
          </Card>

          {/* Items */}
          <Card>
            <BlockStack gap="400">
              <Text as="h2" variant="headingMd">
                Items ({inquiry.items.length})
              </Text>

              <BlockStack gap="300">
                {inquiry.items.map((item) => (
                  <Box
                    key={item.id}
                    padding="300"
                    background="bg-surface-secondary"
                    borderRadius="200"
                  >
                    <InlineStack gap="400" blockAlign="center">
                      <Thumbnail
                        source={item.product.primaryImage || '/placeholder-product.png'}
                        alt={item.product.name}
                        size="medium"
                      />
                      <BlockStack gap="100">
                        <Text as="p" variant="bodyMd" fontWeight="semibold">
                          {item.product.name}
                        </Text>
                        <Text as="p" variant="bodySm" tone="subdued">
                          SKU: {item.product.sku}
                        </Text>
                        <InlineStack gap="200">
                          <Badge>{`Qty: ${item.quantity}`}</Badge>
                          {item.color && <Badge>{item.color}</Badge>}
                        </InlineStack>
                        {item.notes && (
                          <Text as="p" variant="bodySm" tone="subdued">
                            Note: {item.notes}
                          </Text>
                        )}
                      </BlockStack>
                    </InlineStack>
                  </Box>
                ))}
              </BlockStack>
            </BlockStack>
          </Card>

          {/* Quote */}
          {inquiry.quote && (
            <Card>
              <BlockStack gap="400">
                <InlineStack align="space-between">
                  <Text as="h2" variant="headingMd">Quote</Text>
                  <Badge
                    tone={
                      inquiry.quote.status === 'ACCEPTED'
                        ? 'success'
                        : inquiry.quote.status === 'DECLINED' || inquiry.quote.status === 'EXPIRED'
                        ? 'critical'
                        : 'info'
                    }
                  >
                    {inquiry.quote.status}
                  </Badge>
                </InlineStack>

                <BlockStack gap="200">
                  {inquiry.quote.items.map((item) => (
                    <InlineStack key={item.id} align="space-between">
                      <Text as="p" variant="bodyMd">
                        {item.product.name} x {item.quantity}
                      </Text>
                      <Text as="p" variant="bodyMd">
                        {formatCurrency(item.unitPrice)} each = {formatCurrency(item.total)}
                      </Text>
                    </InlineStack>
                  ))}
                </BlockStack>

                <Divider />

                <InlineStack align="space-between">
                  <Text as="p" variant="headingMd">Total</Text>
                  <Text as="p" variant="headingMd" fontWeight="bold">
                    {formatCurrency(inquiry.quote.totalAmount)}
                  </Text>
                </InlineStack>

                <Text as="p" variant="bodySm" tone="subdued">
                  Valid until: {formatDate(inquiry.quote.validityDate)}
                </Text>

                {inquiry.quote.status === 'DRAFT' && (
                  <Button
                    variant="primary"
                    onClick={handleSendQuote}
                    loading={sendingQuote}
                  >
                    Send Quote to Customer
                  </Button>
                )}
              </BlockStack>
            </Card>
          )}

          {/* Messages */}
          <Card>
            <BlockStack gap="400">
              <Text as="h2" variant="headingMd">Messages</Text>

              {inquiry.messages.length === 0 ? (
                <Text as="p" tone="subdued">No messages yet</Text>
              ) : (
                <BlockStack gap="300">
                  {inquiry.messages.map((message: Message) => (
                    <Box
                      key={message.id}
                      padding="300"
                      background={message.senderType === 'ADMIN' ? 'bg-surface-info' : 'bg-surface-secondary'}
                      borderRadius="200"
                    >
                      <BlockStack gap="100">
                        <InlineStack align="space-between">
                          <Text as="p" variant="bodySm" fontWeight="semibold">
                            {message.senderType === 'ADMIN' ? 'Admin' : 'Customer'}
                          </Text>
                          <Text as="p" variant="bodySm" tone="subdued">
                            {formatDate(message.createdAt)}
                          </Text>
                        </InlineStack>
                        <Text as="p" variant="bodyMd">{message.message}</Text>
                      </BlockStack>
                    </Box>
                  ))}
                </BlockStack>
              )}

              <Divider />

              <BlockStack gap="200">
                <TextField
                  label="New Message"
                  value={newMessage}
                  onChange={setNewMessage}
                  multiline={3}
                  autoComplete="off"
                />
                <InlineStack align="end">
                  <Button
                    variant="primary"
                    onClick={handleSendMessage}
                    loading={sendingMessage}
                    disabled={!newMessage.trim()}
                  >
                    Send Message
                  </Button>
                </InlineStack>
              </BlockStack>
            </BlockStack>
          </Card>
        </Layout.Section>

        {/* Sidebar */}
        <Layout.Section variant="oneThird">
          <Card>
            <BlockStack gap="400">
              <Text as="h2" variant="headingMd">Update Status</Text>

              <Select
                label="Status"
                options={STATUS_OPTIONS.map((opt) => ({
                  label: opt.label,
                  value: opt.value,
                }))}
                value={selectedStatus}
                onChange={(value) => setSelectedStatus(value as InquiryStatus)}
              />

              <Button
                variant="primary"
                onClick={handleStatusUpdate}
                loading={saving}
                disabled={!selectedStatus || selectedStatus === inquiry.status}
              >
                Update Status
              </Button>
            </BlockStack>
          </Card>

          <Card>
            <BlockStack gap="300">
              <Text as="h2" variant="headingMd">Actions</Text>

              <Button fullWidth onClick={() => setShowQuoteBuilder(true)}>
                {inquiry.quote ? 'Edit Quote' : 'Create Quote'}
              </Button>

              <Button fullWidth onClick={() => navigate(`/users/${inquiry.user.id}`)}>
                View Customer Profile
              </Button>
            </BlockStack>
          </Card>
        </Layout.Section>
      </Layout>

      {/* Quote Builder Modal */}
      <Modal
        open={showQuoteBuilder}
        onClose={() => setShowQuoteBuilder(false)}
        title={inquiry.quote ? 'Edit Quote' : 'Create Quote'}
        size="large"
      >
        <Modal.Section>
          <QuoteBuilder
            inquiryId={inquiry.id}
            items={inquiry.items}
            existingQuote={inquiry.quote}
            onSave={handleQuoteCreated}
            onCancel={() => setShowQuoteBuilder(false)}
          />
        </Modal.Section>
      </Modal>
    </Page>
  );
}
